/**
 * Conversation State Service
 * 
 * Frontend service for managing conversation state.
 */

import { getAtlasApiBaseUrl } from '../getAtlasApiBaseUrl';
import { atlasFetch } from '../api/atlasBackendClient';

export interface ConversationContext {
  mode?: 'knowledge_query' | 'ticket_creation' | 'mixed' | 'general_chat';
  ticket_data?: Record<string, any>;
  [key: string]: any;
}

export interface ConversationQueryRequest {
  query: string;
  conversation_id?: string;
  conversation_history?: Array<{ role: 'user' | 'assistant'; content: string }>;
  context?: ConversationContext;
}

export interface ConversationQueryResponse {
  answer: string;
  sources: Array<{
    source: string;
    section?: string;
    relevance_score?: number;
  }>;
  confidence: number;
  suggestions: string[];
  requires_clarification: boolean;
  conversation_id: string;
  mode: 'knowledge_query' | 'ticket_creation' | 'mixed' | 'general_chat';
}

export interface CreateConversationResponse {
  conversation_id: string;
  mode: string;
}

export interface GetConversationResponse {
  conversation_id: string;
  mode: string;
  history: Array<{ role: string; content: string }>;
  ticket_data: Record<string, any>;
  context: Record<string, any>;
}

export class ConversationStateService {
  private baseUrl: string;
  private currentConversationId: string | null = null;
  
  constructor() {
    this.baseUrl = getAtlasApiBaseUrl();
  }
  
  /**
   * Create a new conversation session
   */
  async createConversation(): Promise<CreateConversationResponse> {
    try {
      const response = await atlasFetch(`${this.baseUrl}/api/conversation/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(error.detail || `HTTP ${response.status}`);
      }
      
      const result = await response.json() as CreateConversationResponse;
      this.currentConversationId = result.conversation_id;
      return result;
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  }
  
  /**
   * Get conversation state
   */
  async getConversation(conversationId: string): Promise<GetConversationResponse> {
    try {
      const response = await atlasFetch(`${this.baseUrl}/api/conversation/${conversationId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(error.detail || `HTTP ${response.status}`);
      }
      
      return await response.json() as GetConversationResponse;
    } catch (error) {
      console.error('Error getting conversation:', error);
      throw error;
    }
  }
  
  /**
   * Query conversation (send message and get response)
   */
  async queryConversation(
    query: string,
    conversationId?: string,
    conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>,
    context?: ConversationContext
  ): Promise<ConversationQueryResponse> {
    try {
      const convId = conversationId || this.currentConversationId;
      
      const response = await atlasFetch(`${this.baseUrl}/api/conversation/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query,
          conversation_id: convId,
          conversation_history: conversationHistory || [],
          context: context || {},
        } as ConversationQueryRequest),
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(error.detail || `HTTP ${response.status}`);
      }
      
      const result = await response.json() as ConversationQueryResponse;
      this.currentConversationId = result.conversation_id;
      return result;
    } catch (error) {
      console.error('Error querying conversation:', error);
      throw error;
    }
  }
  
  /**
   * Set conversation mode explicitly
   */
  async setMode(
    conversationId: string,
    mode: 'knowledge_query' | 'ticket_creation' | 'mixed' | 'general_chat'
  ): Promise<void> {
    try {
      const response = await atlasFetch(`${this.baseUrl}/api/conversation/${conversationId}/mode`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mode }),
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(error.detail || `HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Error setting conversation mode:', error);
      throw error;
    }
  }
  
  /**
   * Clear conversation (reset to initial state)
   */
  async clearConversation(conversationId: string): Promise<void> {
    try {
      const response = await atlasFetch(`${this.baseUrl}/api/conversation/${conversationId}/clear`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(error.detail || `HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('Error clearing conversation:', error);
      throw error;
    }
  }
  
  /**
   * Get current conversation ID
   */
  getCurrentConversationId(): string | null {
    return this.currentConversationId;
  }
  
  /**
   * Set current conversation ID
   */
  setCurrentConversationId(conversationId: string | null): void {
    this.currentConversationId = conversationId;
  }
}

// Export singleton instance
export const conversationStateService = new ConversationStateService();

